/**
 * Dashboard cat table charts (303/304/305): BaseSelect filters + table search (expanded modal only).
 */

import React from 'react'
import { BaseSelect, type BaseSelectOption } from '../base-components/BaseSelect'

export interface DashboardCatChartFiltersProps {
  chartId: string
  /** Liensearch-by-location chart omits assignee (not a row dimension there). */
  showAssigned?: boolean
  assignedOptions: BaseSelectOption[]
  countyOptions: BaseSelectOption[]
  municipalityOptions: BaseSelectOption[]
  assignedValue?: string
  countyValue?: string
  municipalityValue?: string
  /** Persisted client-side filter for table rows in the modal. */
  tableSearchQuery?: string
  /** Aggregate table to top 20 municipalities by sum of month columns (server-side). */
  top20Municipalities?: boolean
}

const ALL = 'all'

function toApi(v: string) {
  return !v || v === ALL ? '' : v
}

function emitFilters(
  chartId: string,
  showAssigned: boolean,
  assigned: string,
  county: string,
  municipality: string,
) {
  window.dispatchEvent(
    new CustomEvent('lienly:cat-chart-filter', {
      detail: {
        chartId,
        filter_assigned: showAssigned ? toApi(assigned) : '',
        filter_county: toApi(county),
        filter_municipality: toApi(municipality),
      },
    }),
  )
}

function emitTableSearch(chartId: string, query: string) {
  window.dispatchEvent(
    new CustomEvent('lienly:cat-chart-table-search', {
      detail: { chartId, query },
    }),
  )
}

function emitTop20Municipalities(chartId: string, enabled: boolean) {
  window.dispatchEvent(
    new CustomEvent('lienly:cat-chart-top20', {
      detail: { chartId, enabled },
    }),
  )
}

export function DashboardCatChartFilters({
  chartId,
  showAssigned = true,
  assignedOptions,
  countyOptions,
  municipalityOptions,
  assignedValue = '',
  countyValue = '',
  municipalityValue = '',
  tableSearchQuery = '',
  top20Municipalities = false,
}: DashboardCatChartFiltersProps) {
  const norm = (v: string | undefined) => (!v || v === ALL ? ALL : v)
  const [a, setA] = React.useState(() => norm(assignedValue))
  const [c, setC] = React.useState(() => norm(countyValue))
  const [m, setM] = React.useState(() => norm(municipalityValue))
  const [search, setSearch] = React.useState(() => tableSearchQuery || '')
  const [top20, setTop20] = React.useState(() => !!top20Municipalities)

  const aRef = React.useRef(a)
  const cRef = React.useRef(c)
  const mRef = React.useRef(m)
  aRef.current = a
  cRef.current = c
  mRef.current = m

  React.useEffect(() => {
    const na = norm(assignedValue)
    const nc = norm(countyValue)
    const nm = norm(municipalityValue)
    setA(na)
    setC(nc)
    setM(nm)
    aRef.current = na
    cRef.current = nc
    mRef.current = nm
  }, [assignedValue, countyValue, municipalityValue, chartId])

  React.useEffect(() => {
    setSearch(tableSearchQuery || '')
  }, [tableSearchQuery, chartId])

  React.useEffect(() => {
    setTop20(!!top20Municipalities)
  }, [top20Municipalities, chartId])

  React.useEffect(() => {
    const id = window.setTimeout(() => {
      emitTableSearch(chartId, search)
    }, 250)
    return () => window.clearTimeout(id)
  }, [search, chartId])

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6b7280',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  }

  const searchInputId = `chart-cat-search-${chartId}`

  /* Inline flex so layout survives global CSS / theme order. Same row when wide; wrap when narrow. */
  const filtersRowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    columnGap: 14,
    rowGap: 10,
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
  }
  /* flex: none + width min(100%, …) — avoids min-width:auto + % max-width eating a full row */
  const searchWrapStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 'none',
    width: 'min(100%, 22rem)',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  }
  const filterFieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    flex: 'none',
    width: 'min(100%, 14rem)',
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
  }

  return (
    <div className="chart-cat-filters-layout" style={{ width: '100%', minWidth: 0 }}>
      <div className="chart-cat-top20-modal-row">
        <label className="chart-cat-top20-label">
          <input
            type="checkbox"
            className="chart-cat-top20-checkbox"
            checked={top20}
            onChange={(e) => {
              const next = e.target.checked
              setTop20(next)
              emitTop20Municipalities(chartId, next)
            }}
          />
          <span>
            Top 20 municipalities by total{' '}
            <span className="chart-cat-top20-hint">(sum of month columns, descending)</span>
          </span>
        </label>
      </div>
      <div className="chart-cat-filters-inner" style={filtersRowStyle}>
        <div className="chart-cat-search-wrap" style={searchWrapStyle}>
          <label style={labelStyle} htmlFor={searchInputId}>
            Search
          </label>
          <div className="chart-cat-search-field">
            <input
              id={searchInputId}
              type="search"
              className={
                search.trim()
                  ? 'chart-cat-table-search chart-cat-table-search--with-clear'
                  : 'chart-cat-table-search'
              }
              placeholder="Filter visible rows…"
              autoComplete="off"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search.trim() ? (
              <button
                type="button"
                className="chart-cat-search-clear"
                aria-label="Clear search"
                onClick={() => setSearch('')}
              >
                <span aria-hidden>×</span>
              </button>
            ) : null}
          </div>
        </div>
        {showAssigned ? (
          <div className="chart-cat-filter-field" style={filterFieldStyle}>
            <span style={labelStyle}>Assigned</span>
            <BaseSelect
              options={assignedOptions}
              value={a}
              onValueChange={(v) => {
                const next = v || ALL
                setA(next)
                aRef.current = next
                emitFilters(chartId, true, next, cRef.current, mRef.current)
              }}
              placeholder="All"
              allowClear
              clearValue={ALL}
              triggerStyle={{
                width: '100%',
                minWidth: '12rem',
                backgroundColor: 'transparent',
              }}
            />
          </div>
        ) : null}
        <div className="chart-cat-filter-field" style={filterFieldStyle}>
          <span style={labelStyle}>County</span>
          <BaseSelect
            options={countyOptions}
            value={c}
            onValueChange={(v) => {
              const next = v || ALL
              setC(next)
              cRef.current = next
              emitFilters(chartId, showAssigned, aRef.current, next, mRef.current)
            }}
            placeholder="All"
            allowClear
            clearValue={ALL}
            triggerStyle={{
              width: '100%',
              minWidth: '12rem',
              backgroundColor: 'transparent',
            }}
          />
        </div>
        <div className="chart-cat-filter-field" style={filterFieldStyle}>
          <span style={labelStyle}>Municipality</span>
          <BaseSelect
            options={municipalityOptions}
            value={m}
            onValueChange={(v) => {
              const next = v || ALL
              setM(next)
              mRef.current = next
              emitFilters(chartId, showAssigned, aRef.current, cRef.current, next)
            }}
            placeholder="All"
            allowClear
            clearValue={ALL}
            triggerStyle={{
              width: '100%',
              minWidth: '12rem',
              backgroundColor: 'transparent',
            }}
          />
        </div>
      </div>
    </div>
  )
}
